import type {Run} from '../../run';
import type {AssetInfo, EntryGraph, EntryGraphNode, IncludeInfo} from '~/core/markdown';
import type {AnchorIndex} from './types';

import {createHash} from 'node:crypto';
import {join} from 'node:path';

import {bounded} from '~/core/utils';
import {resolveMarkdownPage} from '~/core/markdown';

import {hasFragmentDirective, isVersionedQuery} from './utils';

type CacheItem = {
    signature: string;
    value: Promise<ReadonlySet<string>>;
};

export class AnchorsService {
    private readonly run: Run;

    private readonly cache = new Map<NormalizedPath, CacheItem>();

    private readonly lookups = new Map<NormalizedPath, Promise<ReadonlySet<string>>>();

    private revision = -1;

    constructor(run: Run) {
        this.run = run;
    }

    async index(file: NormalizedPath, assets: AssetInfo[]): Promise<AnchorIndex> {
        const targets = new Set<NormalizedPath>();

        for (const asset of assets) {
            if (
                !asset.hash ||
                asset.hash === '#' ||
                hasFragmentDirective(asset.hash) ||
                isVersionedQuery(asset.search) ||
                (asset.type !== 'link' && asset.type !== 'def')
            ) {
                continue;
            }

            const target = this.resolve((asset.path as NormalizedPath | null) || file);
            if (target && this.run.toc.entrySet.has(target)) {
                targets.add(target);
            }
        }

        const index = new Map<NormalizedPath, ReadonlySet<string>>();
        await Promise.all(
            [...targets].map(async (target) => {
                index.set(target, await this.get(target));
            }),
        );

        return index as AnchorIndex;
    }

    @bounded
    resolve(path: NormalizedPath): NormalizedPath | null {
        return resolveMarkdownPage(path, (candidate) => this.exists(candidate));
    }

    private get(path: NormalizedPath): Promise<ReadonlySet<string>> {
        if (this.revision !== this.run.markdown.revision) {
            this.lookups.clear();
            this.revision = this.run.markdown.revision;
        }

        const cached = this.lookups.get(path);
        if (cached) {
            return cached;
        }

        // Share graph construction and hashing as well as rendering, including in-flight work.
        const value = this.refresh(path).catch((error) => {
            if (this.lookups.get(path) === value) {
                this.lookups.delete(path);
            }
            throw error;
        });
        this.lookups.set(path, value);
        return value;
    }

    private async refresh(path: NormalizedPath): Promise<ReadonlySet<string>> {
        const graph = await this.run.markdown.graph(path);
        const signature = getGraphSignature(graph);
        const cached = this.cache.get(path);
        if (cached?.signature === signature) {
            return cached.value;
        }

        const value = this.collect(path, graph);
        this.cache.set(path, {signature, value});

        try {
            return await value;
        } catch (error) {
            if (this.cache.get(path)?.value === value) {
                this.cache.delete(path);
            }
            throw error;
        }
    }

    private async collect(path: NormalizedPath, graph: EntryGraph): Promise<ReadonlySet<string>> {
        const anchorIds = new Set<string>();
        const {deps, assets} = flattenGraph(graph);

        await this.run.transform(path, graph.content, {
            deps,
            assets,
            anchorIds,
            reportErrors: false,
        });

        return anchorIds;
    }

    private exists(path: NormalizedPath) {
        return this.run.exists(join(this.run.input, path));
    }
}

function flattenGraph(graph: EntryGraph) {
    const deps: IncludeInfo[] = [];
    const assets: AssetInfo[] = [...graph.assets];

    const visit = (node: EntryGraphNode) => {
        deps.push({
            path: node.path,
            link: node.link,
            match: node.match,
            location: node.location,
            hash: node.hash,
            search: node.search,
        });
        assets.push(...node.assets);
        node.deps.forEach(visit);
    };

    graph.deps.forEach(visit);

    return {deps, assets};
}

function getGraphSignature(graph: EntryGraph) {
    const digest = createHash('sha256');

    const visit = (node: Pick<EntryGraph, 'path' | 'content' | 'deps'>) => {
        digest.update(node.path);
        digest.update('\0');
        digest.update(node.content);
        digest.update('\0');
        node.deps.forEach(visit);
    };

    visit(graph);

    return digest.digest('hex');
}
